import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Users, TrendingUp, Link2, Crown, 
  ArrowLeft, Loader2, AlertCircle,
  UserPlus, UserX, ShieldAlert, CalendarDays, Activity,
  Filter, PoundSterling, Radio, Copy, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ADMIN_EMAILS = [
  "diane.shawe@yahoo.com",
  "admin@mydirectdebits.com",
  "support@qualiquiz.app",
  "info@virtualautochat.uk"
];

function StatCard({ icon: Icon, iconBg, iconColor, label, value, testId }) {
  return (
    <div className="card-default p-5" data-testid={testId}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 ${iconBg} rounded-xl flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <span className="text-text-secondary text-sm">{label}</span>
      </div>
      <p className="font-heading font-bold text-dark text-3xl">{value}</p>
    </div>
  );
}

/**
 * UTM Builder — generates campaign-tagged URLs so every channel's traffic gets
 * properly attributed in the Channel Attribution table above.
 */
function UtmBuilderCard() {
  const [base, setBase] = useState('https://mddreminder.uk/');
  const [source, setSource] = useState('youtube');
  const [medium, setMedium] = useState('cpc');
  const [campaign, setCampaign] = useState('pilot-may');
  const [copied, setCopied] = useState(false);

  const buildUrl = () => {
    try {
      const url = new URL(base);
      if (source) url.searchParams.set('utm_source', source.trim());
      if (medium) url.searchParams.set('utm_medium', medium.trim());
      if (campaign) url.searchParams.set('utm_campaign', campaign.trim());
      return url.toString();
    } catch {
      return base;
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildUrl());
      setCopied(true);
      toast.success('Campaign URL copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — please select and copy manually.');
    }
  };

  const PRESETS = [
    { label: 'YouTube Ad', source: 'youtube', medium: 'cpc' },
    { label: 'YouTube organic', source: 'youtube', medium: 'social' },
    { label: 'Google Ad', source: 'google', medium: 'cpc' },
    { label: 'Facebook Ad', source: 'facebook', medium: 'cpc' },
    { label: 'Instagram Ad', source: 'instagram', medium: 'cpc' },
    { label: 'Instagram post', source: 'instagram', medium: 'social' },
    { label: 'LinkedIn post', source: 'linkedin', medium: 'social' },
    { label: 'Email (Brevo)', source: 'brevo', medium: 'email' },
  ];

  return (
    <div className="card-default p-5 mt-4" data-testid="utm-builder-card">
      <h3 className="font-heading font-semibold text-dark mb-2 flex items-center gap-2">
        <Link2 className="w-4 h-4 text-primary" /> Campaign URL Builder (UTM tags)
      </h3>
      <p className="text-sm text-text-secondary mb-3">
        Build a tagged URL for every ad/post so visits and signups land in the right channel above. Tag once → attribute forever.
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        {PRESETS.map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={() => { setSource(p.source); setMedium(p.medium); }}
            className="text-xs bg-gray-100 hover:bg-primary-light hover:text-primary px-2.5 py-1 rounded-full transition"
            data-testid={`utm-preset-${p.source}-${p.medium}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="text-xs text-text-light">Landing URL</label>
          <Input value={base} onChange={(e) => setBase(e.target.value)} placeholder="https://mddreminder.uk/" className="input-field text-sm" data-testid="utm-base-input" />
        </div>
        <div>
          <label className="text-xs text-text-light">Campaign name (e.g. pilot-may)</label>
          <Input value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder="pilot-may" className="input-field text-sm" data-testid="utm-campaign-input" />
        </div>
        <div>
          <label className="text-xs text-text-light">Source (e.g. youtube)</label>
          <Input value={source} onChange={(e) => setSource(e.target.value)} placeholder="youtube" className="input-field text-sm" data-testid="utm-source-input" />
        </div>
        <div>
          <label className="text-xs text-text-light">Medium (cpc = paid, social = organic, email)</label>
          <Input value={medium} onChange={(e) => setMedium(e.target.value)} placeholder="cpc" className="input-field text-sm" data-testid="utm-medium-input" />
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-3 border border-border">
        <p className="text-xs text-text-light mb-1">Your campaign URL:</p>
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
          <code className="text-xs break-all text-primary flex-1 select-all" data-testid="utm-result-url">{buildUrl()}</code>
          <Button onClick={handleCopy} className="btn-primary text-xs py-1.5 px-3 shrink-0" data-testid="utm-copy-btn">
            {copied ? <><Check className="w-3.5 h-3.5 mr-1" /> Copied</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sourceStats, setSourceStats] = useState(null);
  const [signupStats, setSignupStats] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [channelData, setChannelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [youtubeReported, setYoutubeReported] = useState('');
  const [reloadingChannels, setReloadingChannels] = useState(false);

  const isAdmin = user && (
    ADMIN_EMAILS.includes(user.email?.toLowerCase()) || 
    user.is_admin
  );

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/dashboard');
      return;
    }
    
    if (isAdmin) {
      fetchAllStats();
    }
  }, [user, authLoading, isAdmin, navigate]);

  const fetchAllStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const [sourceRes, statsRes, funnelRes, channelsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/source-stats`),
        axios.get(`${API_URL}/api/admin/stats`),
        axios.get(`${API_URL}/api/admin/funnel`),
        axios.get(`${API_URL}/api/admin/channels`),
      ]);
      setSourceStats(sourceRes.data);
      setSignupStats(statsRes.data);
      setFunnelData(funnelRes.data);
      setChannelData(channelsRes.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError(err.response?.data?.detail || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const reloadChannels = async (youtubeReportedNum) => {
    setReloadingChannels(true);
    try {
      const q = youtubeReportedNum ? `?youtube_reported=${encodeURIComponent(youtubeReportedNum)}` : '';
      const { data } = await axios.get(`${API_URL}/api/admin/channels${q}`);
      setChannelData(data);
    } catch (err) {
      console.error('Error reloading channels:', err);
    } finally {
      setReloadingChannels(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center px-4">
        <div className="card-default p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-alert mx-auto mb-4" />
          <h1 className="font-heading font-bold text-dark text-xl mb-2">Access Denied</h1>
          <p className="text-text-secondary mb-6">You don't have permission to view this page.</p>
          <Link to="/dashboard" className="btn-primary inline-block px-6 py-2">Go to Dashboard</Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center px-4">
        <div className="card-default p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-alert mx-auto mb-4" />
          <h1 className="font-heading font-bold text-dark text-xl mb-2">Error Loading Data</h1>
          <p className="text-text-secondary mb-6">{error}</p>
          <button onClick={fetchAllStats} className="btn-primary inline-block px-6 py-2">Try Again</button>
        </div>
      </div>
    );
  }

  const s = signupStats || {};

  return (
    <div className="min-h-screen bg-page">
      <header className="glass-header px-4 py-4">
        <div className="max-w-5xl mx-auto flex items-center gap-4">
          <Link to="/settings" className="text-text-secondary hover:text-primary transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-heading font-semibold text-dark">Admin Analytics</h1>
        </div>
      </header>

      <main className="px-4 py-6 max-w-5xl mx-auto space-y-6">

        {/* User Base */}
        <div data-testid="admin-user-base-section">
          <h2 className="font-heading font-semibold text-dark text-lg mb-3 flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" /> User Base
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              icon={Users} iconBg="bg-primary-light" iconColor="text-primary"
              label="Total Users" value={s.total_users || 0} testId="stat-total-users"
            />
            <StatCard
              icon={UserPlus} iconBg="bg-primary-light" iconColor="text-primary"
              label="Free Users" value={s.free_users || 0} testId="stat-free-users"
            />
            <StatCard
              icon={Crown} iconBg="bg-gold-light" iconColor="text-gold"
              label="Premium Users" value={s.premium_users || 0} testId="stat-premium-users"
            />
          </div>
        </div>

        {/* Signup Activity */}
        <div data-testid="admin-signup-activity-section">
          <h2 className="font-heading font-semibold text-dark text-lg mb-3 flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" /> Signup Activity
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <StatCard
              icon={CalendarDays} iconBg="bg-primary-light" iconColor="text-primary"
              label="Signups Today" value={s.signups_today || 0} testId="stat-signups-today"
            />
            <StatCard
              icon={TrendingUp} iconBg="bg-primary-light" iconColor="text-primary"
              label="Signups This Week" value={s.signups_this_week || 0} testId="stat-signups-week"
            />
          </div>
        </div>

        {/* Signup Funnel / Lost Attempts */}
        <div data-testid="admin-signup-funnel-section">
          <h2 className="font-heading font-semibold text-dark text-lg mb-3 flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-alert" /> Signup Funnel / Lost Attempts
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              icon={UserX} iconBg="bg-alert-light" iconColor="text-alert"
              label="Failed Registrations" value={s.failed_registrations || 0} testId="stat-failed-reg"
            />
            <StatCard
              icon={AlertCircle} iconBg="bg-gold-light" iconColor="text-gold"
              label="Duplicate Email Attempts" value={s.duplicate_email_attempts || 0} testId="stat-duplicate-email"
            />
            <StatCard
              icon={ShieldAlert} iconBg="bg-gray-100" iconColor="text-text-light"
              label="Abandoned Registrations" value={s.abandoned_registrations || 0} testId="stat-abandoned-reg"
            />
          </div>
        </div>

        {/* Conversion Funnel (first-party, MongoDB-driven) */}
        {funnelData && (
          <div data-testid="admin-conversion-funnel-section">
            <h2 className="font-heading font-semibold text-dark text-lg mb-3 flex items-center gap-2">
              <Filter className="w-5 h-5 text-primary" /> Conversion Funnel
              <span className="text-xs font-normal text-text-light ml-1">(first-party, no trackers)</span>
            </h2>
            <div className="card-default p-5 space-y-3" data-testid="admin-conversion-funnel-card">
              {funnelData.funnel.map((stage, idx) => {
                const widthPct = Math.max(stage.from_top_pct, 4); // min 4% for visibility
                return (
                  <div key={stage.key} data-testid={`funnel-stage-${stage.key}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium text-dark">
                        {idx + 1}. {stage.label}
                      </span>
                      <span className="text-sm text-text-secondary">
                        <span className="font-semibold text-dark">{stage.count.toLocaleString()}</span>
                        <span className="ml-2 text-xs">
                          {stage.from_top_pct.toFixed(1)}% of signups
                          {idx > 0 && ` · ${stage.from_prev_pct.toFixed(1)}% step`}
                        </span>
                      </span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all duration-500"
                        style={{ width: `${widthPct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Revenue */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
              <StatCard
                icon={PoundSterling} iconBg="bg-gold-light" iconColor="text-gold"
                label="Total Revenue (paid)"
                value={`£${(funnelData.revenue.total_gbp || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                testId="stat-total-revenue"
              />
              <StatCard
                icon={TrendingUp} iconBg="bg-primary-light" iconColor="text-primary"
                label="Paid Transactions"
                value={funnelData.revenue.total_paid_transactions || 0}
                testId="stat-paid-transactions"
              />
              <div className="card-default p-5" data-testid="stat-revenue-by-plan">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-primary-light rounded-xl flex items-center justify-center">
                    <Crown className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-text-secondary text-sm">Revenue by Plan</span>
                </div>
                {Object.keys(funnelData.revenue.by_plan || {}).length === 0 ? (
                  <p className="text-text-light text-sm">No paid transactions yet</p>
                ) : (
                  <div className="space-y-1">
                    {Object.entries(funnelData.revenue.by_plan).map(([plan, info]) => (
                      <div key={plan} className="flex justify-between text-sm">
                        <span className="capitalize text-dark">{plan}</span>
                        <span className="font-semibold text-dark">
                          £{(info.revenue || 0).toFixed(2)} <span className="text-text-light text-xs">({info.count})</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Channel Attribution — multi-source acquisition breakdown */}
        {channelData && (
          <div data-testid="admin-channels-section" className="mb-6">
            <h2 className="font-heading font-semibold text-dark text-lg mb-3 flex items-center gap-2">
              <Radio className="w-5 h-5 text-primary" /> Channel Attribution
              <span className="text-xs font-normal text-text-light ml-1">(YouTube · Google · AI Search · Blog · Social · Direct)</span>
            </h2>
            <div className="card-default overflow-hidden">
              {/* YouTube reality check */}
              <div className="p-4 bg-gold-light/30 border-b border-border">
                <p className="text-xs font-semibold text-dark mb-2">YouTube Reality Check — compare what YouTube reports vs what reaches your site:</p>
                <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                  <Input
                    type="number"
                    value={youtubeReported}
                    onChange={(e) => setYoutubeReported(e.target.value)}
                    placeholder="Paste YouTube's 'Website visits' number"
                    className="input-field text-sm flex-1"
                    data-testid="youtube-reported-input"
                  />
                  <Button
                    onClick={() => reloadChannels(youtubeReported)}
                    disabled={!youtubeReported || reloadingChannels}
                    className="btn-secondary text-sm py-2 px-4"
                    data-testid="youtube-reality-check-btn"
                  >
                    {reloadingChannels ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Compare'}
                  </Button>
                </div>
                {channelData.youtube_reality_check && (
                  <div className="mt-3 text-sm bg-white rounded-lg p-3 border border-border" data-testid="youtube-reality-check-result">
                    <p>YouTube reported: <span className="font-semibold">{channelData.youtube_reality_check.youtube_reported.toLocaleString()}</span> visits</p>
                    <p>Actually tracked on our site: <span className="font-semibold">{channelData.youtube_reality_check.our_tracked.toLocaleString()}</span></p>
                    <p className="text-text-secondary text-xs mt-1">
                      Tracking loss: <span className="font-semibold">{channelData.youtube_reality_check.tracking_loss_pct}%</span> — typical industry range is 50–70% loss due to ad-blockers, consent rejection, and click farms. <strong>Reality is roughly in the middle.</strong>
                    </p>
                  </div>
                )}
              </div>

              {channelData.rows.length === 0 ? (
                <div className="p-8 text-center text-text-secondary">
                  <p>No channel data yet. Tag your campaign URLs with UTM parameters (use the builder below) and visits will be attributed here.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-border">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase">Channel</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-text-secondary uppercase">Visits</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-text-secondary uppercase">Signups</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-text-secondary uppercase hidden sm:table-cell">Conv%</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-text-secondary uppercase">Premium</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {channelData.rows.map((row) => (
                        <tr key={row.channel} className="hover:bg-gray-50" data-testid={`channel-row-${row.channel}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${row.is_paid ? 'bg-gold' : 'bg-primary'}`} />
                              <span className="font-medium text-dark">{row.label}</span>
                              {row.is_paid && <span className="text-xs bg-gold-light text-gold-dark px-1.5 py-0.5 rounded">paid</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-dark">{row.visits.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right font-semibold text-primary">{row.signups.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right text-text-secondary hidden sm:table-cell">
                            {row.visit_to_signup_rate !== null ? `${row.visit_to_signup_rate}%` : '—'}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-gold">{row.premium}</td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50 font-semibold">
                        <td className="px-4 py-3 text-dark">Total</td>
                        <td className="px-4 py-3 text-right text-dark">{channelData.totals.visits.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-primary">{channelData.totals.signups.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-text-secondary hidden sm:table-cell">
                          {channelData.totals.visit_to_signup_rate !== null ? `${channelData.totals.visit_to_signup_rate}%` : '—'}
                        </td>
                        <td className="px-4 py-3 text-right text-gold">{channelData.totals.premium}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <UtmBuilderCard />
          </div>
        )}

        {/* Source Performance (existing) */}
        <div data-testid="admin-source-performance-section">
          <div className="card-default overflow-hidden">
            <div className="p-5 border-b border-border">
              <h2 className="font-heading font-semibold text-dark text-lg flex items-center gap-2">
                <Link2 className="w-5 h-5 text-gold" /> Source Performance
              </h2>
              <p className="text-sm text-text-secondary mt-1">Signups by referral source</p>
            </div>

            {sourceStats?.sources?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-border">
                    <tr>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wider">Source</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wider">Signups</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wider">Premium</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wider hidden sm:table-cell">First Signup</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wider hidden sm:table-cell">Last Signup</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {sourceStats.sources.map((source, i) => (
                      <tr key={i} className="hover:bg-gray-50 transition-colors">
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            {source.source === "Direct (no source)" ? (
                              <span className="text-text-light">{source.source}</span>
                            ) : (
                              <>
                                <Link2 className="w-4 h-4 text-primary" />
                                <span className="font-medium text-dark">{source.source}</span>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <span className="font-semibold text-dark">{source.total_signups}</span>
                        </td>
                        <td className="px-5 py-4 text-right">
                          {source.premium_users > 0 ? (
                            <span className="inline-flex items-center gap-1 text-gold">
                              <Crown className="w-3 h-3" /> {source.premium_users}
                            </span>
                          ) : (
                            <span className="text-text-light">0</span>
                          )}
                        </td>
                        <td className="px-5 py-4 text-right text-sm text-text-secondary hidden sm:table-cell">
                          {source.first_signup ? new Date(source.first_signup).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-5 py-4 text-right text-sm text-text-secondary hidden sm:table-cell">
                          {source.last_signup ? new Date(source.last_signup).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center">
                <Users className="w-12 h-12 text-text-light mx-auto mb-3" />
                <p className="text-text-secondary">No signup data yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/share" className="btn-primary px-6 py-2">Generate Share Links</Link>
          <Link to="/admin/promos" data-testid="admin-promos-link" className="btn-secondary px-6 py-2">Promo Codes</Link>
          <Link to="/admin/blog" data-testid="admin-blog-link" className="btn-secondary px-6 py-2">Blog Posts</Link>
          <button onClick={fetchAllStats} className="btn-secondary px-6 py-2" data-testid="refresh-admin-stats">Refresh Data</button>
        </div>
      </main>
    </div>
  );
}
